import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import PageHeader from '../../../components/PageHeader';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { useModuleLicense } from '../../../hooks/useModuleLicense';
import pbrService from '../../../services/pbr';
import userService, { type UserData } from '../../../services/user';
import organizationService, { type Organization } from '../../../services/organization';
import {
  qualifiedPersonSchema,
  type QualifiedPersonFormData,
  type QualifiedPerson,
} from '../../../types/cultivar';
import { UserCheck, Plus, X, Edit2, Trash2, ShieldAlert } from 'lucide-react';

export default function QualifiedPersonsRegister() {
  const { hasAccess, loading: licLoading } = useModuleLicense('cultivar');
  const [rows, setRows] = useState<QualifiedPerson[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<QualifiedPerson | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    pbrService.listQualifiedPersons()
      .then(r => { if (r.success && r.data) setRows(r.data); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (hasAccess) load(); else setLoading(false); }, [hasAccess]);

  if (licLoading) return <div className="p-6 text-neutral-500">Loading…</div>;
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ShieldAlert className="h-12 w-12 text-neutral-300 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-neutral-700">Access Restricted</h2>
        </div>
      </div>
    );
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Delete this QP? This cannot be undone.')) return;
    const r = await pbrService.deleteQualifiedPerson(id);
    if (r.success) load();
    else setError(r.message || 'Delete failed');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Qualified Persons Register"
        subtitle="People accredited to sign the Nomination of a Qualified Person for PBR applications"
        icon={<UserCheck className="h-6 w-6 text-white" />}
        actionButtons={
          <Button onClick={() => { setEditing(null); setShowForm(true); }}>
            <Plus className="h-4 w-4 mr-1" /> Add QP
          </Button>
        }
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-4">
        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded">{error}</div>}

        {showForm && (
          <QpForm
            initial={editing}
            onClose={() => setShowForm(false)}
            onSaved={() => { setShowForm(false); load(); }}
          />
        )}

        {loading ? (
          <div className="text-neutral-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
            <p className="text-neutral-600">No Qualified Persons registered yet.</p>
          </div>
        ) : (
          <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-neutral-600 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Accreditation</th>
                  <th className="px-4 py-3 font-medium">Species</th>
                  <th className="px-4 py-3 font-medium">Contact</th>
                  <th className="px-4 py-3 font-medium">Role</th>
                  <th className="px-4 py-3 font-medium">Active</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {rows.map(qp => (
                  <tr key={qp.id}>
                    <td className="px-4 py-3 font-medium">{qp.full_name}</td>
                    <td className="px-4 py-3">{qp.accreditation_number || '—'}</td>
                    <td className="px-4 py-3">
                      {qp.accredited_species.length > 0
                        ? qp.accredited_species.join(', ')
                        : <span className="text-neutral-400">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      {qp.email && <div>{qp.email}</div>}
                      {qp.phone && <div className="text-xs text-neutral-500">{qp.phone}</div>}
                    </td>
                    <td className="px-4 py-3">
                      {qp.is_external_consultant ? 'External consultant' : 'Internal'}
                    </td>
                    <td className="px-4 py-3">
                      {qp.active
                        ? <span className="text-emerald-600">✓</span>
                        : <span className="text-neutral-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => { setEditing(qp); setShowForm(true); }} className="text-primary-600 hover:underline mr-3">
                        <Edit2 className="h-4 w-4 inline" />
                      </button>
                      <button onClick={() => handleDelete(qp.id)} className="text-red-600 hover:underline">
                        <Trash2 className="h-4 w-4 inline" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

interface FormProps {
  initial: QualifiedPerson | null;
  onClose: () => void;
  onSaved: () => void;
}

function QpForm({ initial, onClose, onSaved }: FormProps) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [speciesInput, setSpeciesInput] = useState('');
  const [species, setSpecies] = useState<string[]>(initial?.accredited_species || []);
  const [orgUsers, setOrgUsers] = useState<UserData[]>([]);
  const [org, setOrg] = useState<Organization | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string>('');

  const { register, handleSubmit, setValue, formState: { errors } } = useForm<QualifiedPersonFormData>({
    resolver: zodResolver(qualifiedPersonSchema),
    defaultValues: {
      full_name: initial?.full_name || '',
      email: initial?.email || '',
      phone: initial?.phone || '',
      address_line1: initial?.address_line1 || '',
      address_line2: initial?.address_line2 || '',
      suburb: initial?.suburb || '',
      state: initial?.state || '',
      postcode: initial?.postcode || '',
      country: initial?.country || 'Australia',
      accreditation_number: initial?.accreditation_number || '',
      is_external_consultant: initial?.is_external_consultant ?? false,
      active: initial?.active ?? true,
      notes: initial?.notes || '',
    },
  });

  // Only load prefill sources when adding a new QP (not when editing).
  useEffect(() => {
    if (initial) return;
    userService.getUsers().then(r => {
      if (r.success && r.data) setOrgUsers(r.data);
    }).catch(() => {});
    organizationService.getCurrentOrganization().then(r => {
      if (r.success && r.data) setOrg(r.data);
    }).catch(() => {});
  }, [initial]);

  const prefillFromUser = (userIdStr: string) => {
    setSelectedUserId(userIdStr);
    if (!userIdStr) return;
    const u = orgUsers.find(x => String(x.id) === userIdStr);
    if (!u) return;
    const fullName = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
    const opts = { shouldDirty: true, shouldValidate: false } as const;
    if (fullName) setValue('full_name', fullName, opts);
    if (u.email) setValue('email', u.email, opts);
    if (org) {
      if (org.contact_phone) setValue('phone', org.contact_phone, opts);
      if (org.address_line1) setValue('address_line1', org.address_line1, opts);
      if (org.address_line2) setValue('address_line2', org.address_line2, opts);
      if (org.city) setValue('suburb', org.city, opts);
      if (org.state) setValue('state', org.state, opts);
      if (org.postcode) setValue('postcode', org.postcode, opts);
      if (org.country) setValue('country', org.country, opts);
    }
  };

  const addSpecies = () => {
    const v = speciesInput.trim();
    if (v && !species.includes(v)) setSpecies([...species, v]);
    setSpeciesInput('');
  };

  const onSubmit = async (data: QualifiedPersonFormData) => {
    setSubmitting(true);
    setError(null);
    const payload = { ...data, accredited_species: species };
    const r = initial
      ? await pbrService.updateQualifiedPerson(initial.id, payload)
      : await pbrService.createQualifiedPerson(payload);
    setSubmitting(false);
    if (r.success) onSaved();
    else setError(r.message || 'Save failed');
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">{initial ? 'Edit QP' : 'Add QP'}</h3>
        <button onClick={onClose}><X className="h-5 w-5 text-neutral-400" /></button>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {!initial && orgUsers.length > 0 && (
          <div className="bg-primary-50 border border-primary-100 rounded-lg p-3">
            <label className="block text-sm font-semibold text-primary-800 mb-1">
              Prefill from team member
            </label>
            <p className="text-xs text-primary-700 mb-2">
              Pick a user from your organisation to fill in name, email, and the org address.
            </p>
            <select
              value={selectedUserId}
              onChange={e => prefillFromUser(e.target.value)}
              className="input-professional w-full md:w-1/2"
            >
              <option value="">— Select user —</option>
              {orgUsers.map(u => {
                const label = [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.username;
                return (
                  <option key={u.id} value={u.id}>
                    {label}{u.email ? ` (${u.email})` : ''}
                  </option>
                );
              })}
            </select>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Full name *" {...register('full_name')} error={errors.full_name?.message} />
          <Input label="Accreditation number" {...register('accreditation_number')} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Email" type="email" {...register('email')} error={errors.email?.message} />
          <Input label="Phone" {...register('phone')} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Address line 1" {...register('address_line1')} />
          <Input label="Address line 2" {...register('address_line2')} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Input label="Suburb" {...register('suburb')} />
          <Input label="State" {...register('state')} />
          <Input label="Postcode" {...register('postcode')} />
          <Input label="Country" {...register('country')} />
        </div>

        <div>
          <label className="block text-sm font-semibold text-neutral-700 mb-1">Accredited species</label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={speciesInput}
              onChange={e => setSpeciesInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSpecies(); } }}
              placeholder="e.g., Malus domestica"
              className="input-professional flex-1"
            />
            <Button type="button" variant="ghost" onClick={addSpecies}>Add</Button>
          </div>
          <div className="flex flex-wrap gap-1">
            {species.map((s, i) => (
              <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-neutral-100 rounded text-sm">
                {s}
                <button type="button" onClick={() => setSpecies(species.filter((_, j) => j !== i))}>
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        </div>

        <div className="flex gap-6">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" {...register('is_external_consultant')} />
            External consultant
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" {...register('active')} />
            Active
          </label>
        </div>

        <div>
          <label className="block text-sm font-semibold text-neutral-700 mb-1">Notes</label>
          <textarea {...register('notes')} rows={2} className="input-professional w-full" />
        </div>

        {error && <div className="text-red-600 text-sm">{error}</div>}

        <div className="flex justify-end gap-3 pt-4 border-t border-neutral-100">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={submitting}>{submitting ? 'Saving…' : 'Save'}</Button>
        </div>
      </form>
    </div>
  );
}
